import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Paper,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteIcon from '@mui/icons-material/Delete';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import { DataService } from 'src/services/data-service';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';

interface TenantFileUploadDialogProps {
  open: boolean;
  tenantId: string;
  tenantName: string;
  onClose: () => void;
  onSuccess: () => void;
}

const TenantFileUploadDialog: React.FC<TenantFileUploadDialogProps> = ({
  open,
  tenantId,
  tenantName,
  onClose,
  onSuccess
}) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const dispatch = useAppDispatch();

  // Reset files when dialog opens
  React.useEffect(() => {
    if (open) {
      setSelectedFiles([]);
    }
  }, [open]);

  // Handle file selection
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    setSelectedFiles(prev => [...prev, ...files]);
  };

  // Remove selected file
  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Get file icon based on type
  const getFileIcon = (file: File) => {
    const type = file.type.toLowerCase();
    if (type.includes('image')) {
      return <ImageIcon color="primary" />;
    } else if (type.includes('pdf')) {
      return <PictureAsPdfIcon color="error" />;
    } else if (type.includes('doc') || type.includes('word')) {
      return <DescriptionIcon color="info" />;
    } else {
      return <AttachFileIcon color="action" />;
    }
  };

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Handle file upload
  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      dispatch(showAlert({
        message: 'Please select at least one file to upload',
        type: 'warning'
      }));
      return;
    }

    setUploading(true);

    try {
      // Create FormData for multipart/form-data
      const formData = new FormData();

      selectedFiles.forEach(file => {
        formData.append('files', file);
      });

      // Debug: Log what we're sending
      console.log('Uploading files for tenant:', tenantId);
      console.log('Selected files:', selectedFiles.map(f => f.name));

      const response = await DataService.postForm(`api/Tenant/upload-files/${tenantId}`, formData);

      if (response.ok) {
        dispatch(showAlert({
          message: `Successfully uploaded ${selectedFiles.length} file${selectedFiles.length > 1 ? 's' : ''}!`,
          type: 'success'
        }));
        onSuccess();
      } else {
        // Try to get error details from response
        let errorMessage = 'Failed to upload files';
        try {
          const errorData = await response.json();
          console.error('API Error Response:', errorData);
          if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.errors) {
            const validationErrors = Object.values(errorData.errors).flat();
            errorMessage = validationErrors.join(', ');
          }
        } catch (e) {
          errorMessage = `Failed to upload files: ${response.status} ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Error uploading files:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to upload files. Please try again.';
      dispatch(showAlert({
        message: errorMessage,
        type: 'error'
      }));
    } finally {
      setUploading(false);
    }
  };

  // Handle dialog close
  const handleClose = () => {
    if (!uploading) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Typography variant="h6" component="div">
          Upload Files
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Add additional documents for {tenantName}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* File Upload Area */}
          <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', position: 'relative' }}>
            <input
              accept="image/*,.pdf,.doc,.docx"
              style={{ display: 'none' }}
              id="file-upload-additional"
              multiple
              type="file"
              onChange={handleFileChange}
              disabled={uploading}
            />
            <label htmlFor="file-upload-additional">
              <Button
                variant="outlined"
                component="span"
                startIcon={<CloudUploadIcon />}
                disabled={uploading}
                size="large"
                sx={{ mb: 2 }}
              >
                Select Files
              </Button>
            </label>
            <Typography variant="body2" color="text.secondary">
              Supported formats: Images, PDF, Word documents
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              Maximum file size: 10MB per file
            </Typography>
          </Paper>

          {/* Selected Files List */}
          {selectedFiles.length > 0 && (
            <Box>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium' }}>
                Selected Files ({selectedFiles.length})
              </Typography>
              <List dense>
                {selectedFiles.map((file, index) => (
                  <ListItem
                    key={index}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      mb: 1,
                      pr: 1
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {getFileIcon(file)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          {file.name}
                        </Typography>
                      }
                      secondary={
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Type: {file.type || 'Unknown'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                            Size: {formatFileSize(file.size)}
                          </Typography>
                        </Box>
                      }
                    />
                    <IconButton
                      edge="end"
                      onClick={() => removeFile(index)}
                      disabled={uploading}
                      color="error"
                      size="small"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {/* Upload Information */}
          <Box
            sx={{
              p: 2,
              backgroundColor: 'rgba(0, 0, 0, 0.02)',
              borderRadius: 1,
              border: '1px solid rgba(0, 0, 0, 0.12)'
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 0.5 }}>
              Upload Information
            </Typography>
            <Typography variant="body2" color="text.secondary">
              These files will be attached to the tenant's profile and can be downloaded later.
              All files are securely stored and only accessible to authorized users.
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button
          onClick={handleClose}
          disabled={uploading}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          onClick={handleUpload}
          disabled={uploading || selectedFiles.length === 0}
          variant="contained"
          startIcon={uploading ? <CircularProgress size={16} /> : <CloudUploadIcon />}
        >
          {uploading ? 'Uploading...' : `Upload ${selectedFiles.length} File${selectedFiles.length !== 1 ? 's' : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TenantFileUploadDialog;